import React, { useEffect, useRef, useState } from "react";
import { api, useLang, usePerm, formatApiError } from "../lib/providers";
import { PageHeader, Btn, Field, TextInput, TextArea, Section } from "../components/primitives";
import VatRatesManager from "../components/VatRatesManager";
import { toast as sonner, toast } from "sonner";
import { FloppyDisk, UploadSimple, Trash, Image as ImageIcon, Warning, X } from "@phosphor-icons/react";

const empty = { name: "", address: "", email: "", phone: "", tax_id: "", bank_info: "", logo: "", agb_text: "" };

export default function SettingsPage() {
  const { t, lang } = useLang();
  const { isAdmin } = usePerm();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const fileRef = useRef(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [numberingResetOpen, setNumberingResetOpen] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then((r) => setForm({ ...empty, ...r.data })).finally(() => setLoading(false));
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      sonner.error(lang === "de" ? "Max. 500 KB" : "Max 500 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, logo: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/company", form);
      setToast(t("saved"));
      setTimeout(() => setToast(""), 2000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-[var(--muted)]">…</div>;

  return (
    <div className="p-6 sm:p-8 max-w-[1000px] mx-auto">
      <PageHeader
        eyebrow={lang === "de" ? "07 / Einstellungen" : "07 / Settings"}
        title={lang === "de" ? "Firmenprofil" : "Company profile"}
        description={lang === "de" ? "Diese Angaben erscheinen auf deinen PDF-Angeboten." : "These details appear on your quote PDFs."}
        actions={
          <Btn onClick={save} disabled={saving} data-testid="settings-save">
            <FloppyDisk size={16} /> {saving ? "…" : t("save")}
          </Btn>
        }
      />

      {toast && <div className="mb-4 text-sm text-[#047857] border border-[#10B981]/30 bg-[#10B981]/5 px-3 py-2 rounded-sm">{toast}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title={lang === "de" ? "Angaben" : "Details"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={lang === "de" ? "Firmenname" : "Company name"} className="md:col-span-2">
                <TextInput data-testid="settings-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label={t("address")} className="md:col-span-2">
                <TextArea data-testid="settings-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </Field>
              <Field label={t("email")}>
                <TextInput type="email" data-testid="settings-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label={t("phone")}>
                <TextInput data-testid="settings-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Field>
              <Field label={t("taxId")}>
                <TextInput data-testid="settings-tax-id" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} />
              </Field>
              <Field label={lang === "de" ? "Bankverbindung" : "Bank info"} className="md:col-span-2">
                <TextArea data-testid="settings-bank" value={form.bank_info} onChange={(e) => setForm({ ...form, bank_info: e.target.value })} />
              </Field>
              <Field label={lang === "de" ? "AGB-Hinweis (klein im PDF-Footer)" : "Terms & Conditions footer note (PDF)"} className="md:col-span-2">
                <TextArea data-testid="settings-agb" rows={2} placeholder={lang === "de" ? "z. B. Es gelten unsere AGB unter www.deine-firma.de/agb" : "e.g. Our terms apply, see www.your-co.com/terms"} value={form.agb_text} onChange={(e) => setForm({ ...form, agb_text: e.target.value })} />
              </Field>
            </div>
          </Section>
        </div>

        <div>
          <Section title={lang === "de" ? "Logo" : "Logo"}>
            <div className="border border-dashed border-[var(--border)] rounded-sm bg-[var(--bg)] p-6 text-center">
              {form.logo ? (
                <div>
                  <img src={form.logo} alt="Logo" className="max-h-24 mx-auto object-contain" data-testid="settings-logo-preview" />
                </div>
              ) : (
                <div className="text-[var(--muted-2)] py-6">
                  <ImageIcon size={32} className="mx-auto mb-2" />
                  <div className="text-xs">{lang === "de" ? "Noch kein Logo" : "No logo yet"}</div>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={onFile} className="hidden" data-testid="settings-logo-input" />
            <div className="flex gap-2 mt-3">
              <Btn variant="secondary" onClick={() => fileRef.current?.click()} className="flex-1" data-testid="settings-logo-upload">
                <UploadSimple size={14} /> {lang === "de" ? "Hochladen" : "Upload"}
              </Btn>
              {form.logo && (
                <Btn variant="ghost" onClick={() => setForm({ ...form, logo: "" })} data-testid="settings-logo-remove" className="text-[#FF3B30] hover:bg-[#FF3B30]/5">
                  <Trash size={14} />
                </Btn>
              )}
            </div>
            <div className="text-xs text-[var(--muted-2)] mt-3">
              PNG / JPEG · max 500 KB
            </div>
          </Section>
        </div>
      </div>

      {isAdmin && (
        <Section className="mt-8">
          <VatRatesManager />
        </Section>
      )}

      {isAdmin && (
        <div className="mt-12 space-y-4">
          {/* Manual counter adjustment — set next-number without wiping data */}
          <CounterCard lang={lang} />

          {/* Numbering reset — soft option, keeps master data */}
          <div className="border border-[#F59E0B]/40 bg-[#F59E0B]/[0.04] rounded-sm p-6" data-testid="settings-numbering-reset">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-[#F59E0B]/15 text-[#F59E0B] shrink-0">
                <Warning size={20} weight="fill" />
              </div>
              <div className="flex-1">
                <div className="label-overline text-[#F59E0B] mb-1">{lang === "de" ? "Nummernkreise" : "Numbering"}</div>
                <h3 className="font-heading text-lg font-semibold text-[var(--ink)]">
                  {lang === "de" ? "Nummernkreise zurücksetzen" : "Reset numbering"}
                </h3>
                <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">
                  {lang === "de"
                    ? "Löscht alle bestehenden Angebote, Rechnungen, Gutschriften und Aufmaße deiner Organisation. Die nächsten Dokumente starten wieder bei 0100. Kunden, Positionen, Kategorien, MwSt-Sätze und Firmen-Einstellungen bleiben erhalten."
                    : "Deletes all existing quotes, invoices, credit notes and measurement protocols. The next documents will start at 0100. Customers, positions, categories, VAT rates and company settings are preserved."}
                </p>
                <div className="mt-4">
                  <Btn variant="ghost" onClick={() => setNumberingResetOpen(true)} data-testid="numbering-reset-open">
                    <Trash size={14} /> {lang === "de" ? "Nummernkreise zurücksetzen" : "Reset numbering"}
                  </Btn>
                </div>
              </div>
            </div>
          </div>

          {/* Full demo-reset — hard option, wipes catalog too */}
          <div className="border border-[#FF3B30]/30 bg-[#FF3B30]/[0.03] rounded-sm p-6" data-testid="settings-danger-zone">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-[#FF3B30]/10 text-[#FF3B30] shrink-0">
                <Warning size={20} weight="fill" />
              </div>
              <div className="flex-1">
                <div className="label-overline text-[#FF3B30] mb-1">{lang === "de" ? "Gefahrenzone" : "Danger zone"}</div>
                <h3 className="font-heading text-lg font-semibold text-[var(--ink)]">
                  {lang === "de" ? "Demo-Daten komplett löschen" : "Wipe demo data"}
                </h3>
                <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">
                  {lang === "de"
                    ? "Löscht zusätzlich zu den Dokumenten auch alle Kunden, Positionen und Kategorien. Firmen-Einstellungen, MwSt-Sätze und Nutzerkonten bleiben erhalten."
                    : "In addition to documents, also deletes all customers, positions and categories. Company settings, VAT rates and user accounts are preserved."}
                </p>
                <div className="mt-4">
                  <Btn variant="danger" onClick={() => setResetOpen(true)} data-testid="settings-reset-open">
                    <Trash size={14} /> {lang === "de" ? "Demo-Daten komplett löschen" : "Wipe demo data"}
                  </Btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {resetOpen && <DemoResetModal lang={lang} onClose={() => setResetOpen(false)} />}
      {numberingResetOpen && <NumberingResetModal lang={lang} onClose={() => setNumberingResetOpen(false)} />}
    </div>
  );
}

function DemoResetModal({ lang, onClose }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const REQUIRED = "RESET";
  const canSubmit = confirmText.trim().toUpperCase() === REQUIRED && !busy;

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/demo-reset", {
        confirm: "RESET",
        keep_categories: false,
        keep_positions: false,
        keep_settings: true,
      });
      setResult(data.deleted || {});
      sonner.success(lang === "de" ? "Demo-Daten erfolgreich zurückgesetzt" : "Demo data reset successfully");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-[100]"
      onClick={() => !busy && onClose()}
      data-testid="reset-modal-overlay"
    >
      <div
        className="swiss-card w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="reset-modal"
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-[#FF3B30]/10 text-[#FF3B30] shrink-0">
            <Warning size={20} weight="fill" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-semibold text-[var(--ink)]">
              {lang === "de" ? "Demo-Daten zurücksetzen?" : "Reset demo data?"}
            </h3>
            <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">
              {lang === "de"
                ? "Diese Aktion kann nicht rückgängig gemacht werden. Folgendes wird gelöscht:"
                : "This action cannot be undone. The following will be deleted:"}
            </p>
            <ul className="text-sm text-[var(--muted)] mt-2 ml-5 list-disc space-y-0.5">
              <li>{lang === "de" ? "Alle Kunden" : "All customers"}</li>
              <li>{lang === "de" ? "Alle Angebote" : "All quotes"}</li>
              <li>{lang === "de" ? "Alle Rechnungen & Gutschriften" : "All invoices & credit notes"}</li>
              <li>{lang === "de" ? "Alle Aufmaße" : "All measurement protocols"}</li>
              <li>{lang === "de" ? "Positionen & Kategorien" : "Positions & categories"}</li>
            </ul>
            <p className="text-sm text-[var(--muted)] mt-3 leading-relaxed">
              {lang === "de"
                ? <>Bleiben erhalten: <b>Firmen-Einstellungen</b> & <b>Nutzerkonten</b>.</>
                : <>Preserved: <b>company settings</b> & <b>user accounts</b>.</>}
            </p>
          </div>
          <button onClick={onClose} disabled={busy} className="text-[var(--muted)] hover:text-[var(--ink)] disabled:opacity-50" data-testid="reset-modal-close">
            <X size={18} />
          </button>
        </div>

        {result ? (
          <div className="mt-4 border border-[var(--border)] bg-[var(--row-hover)] rounded-sm p-4" data-testid="reset-result">
            <div className="text-sm font-medium text-[var(--ink)] mb-2">
              {lang === "de" ? "Erfolgreich zurückgesetzt" : "Reset successful"}
            </div>
            <div className="text-xs text-[var(--muted)] space-y-1 font-mono">
              {Object.entries(result).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span>{k}</span>
                  <span className="text-[var(--ink)]">−{v}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <Btn variant="primary" onClick={onClose} data-testid="reset-modal-done">
                {lang === "de" ? "Schließen" : "Close"}
              </Btn>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-4">
              <label className="label-overline block mb-1.5">
                {lang === "de"
                  ? <>Tippe <code className="text-[var(--ink)] bg-[var(--row-hover)] px-1 rounded-sm">RESET</code> zur Bestätigung</>
                  : <>Type <code className="text-[var(--ink)] bg-[var(--row-hover)] px-1 rounded-sm">RESET</code> to confirm</>}
              </label>
              <TextInput
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="RESET"
                autoFocus
                disabled={busy}
                data-testid="reset-modal-confirm-input"
              />
            </div>
            {error && (
              <div className="mt-3 text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 rounded-sm" data-testid="reset-modal-error">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={onClose} disabled={busy} data-testid="reset-modal-cancel">
                {lang === "de" ? "Abbrechen" : "Cancel"}
              </Btn>
              <Btn variant="danger" onClick={submit} disabled={!canSubmit} data-testid="reset-modal-submit">
                <Trash size={14} /> {busy
                  ? (lang === "de" ? "Wird gelöscht…" : "Deleting…")
                  : (lang === "de" ? "Endgültig löschen" : "Delete permanently")}
              </Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function NumberingResetModal({ lang, onClose }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const REQUIRED = "RESET";
  const canSubmit = confirmText.trim().toUpperCase() === REQUIRED && !busy;

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/demo-reset", {
        confirm: "RESET",
        keep_customers: true,
        keep_categories: true,
        keep_positions: true,
        keep_settings: true,
      });
      setResult(data.deleted || {});
      sonner.success(lang === "de" ? "Nummernkreise zurückgesetzt — neue Dokumente starten bei 0100" : "Numbering reset — new documents start at 0100");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-[100]"
      onClick={() => !busy && onClose()}
      data-testid="numbering-reset-overlay"
    >
      <div className="swiss-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="numbering-reset-modal">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-[#F59E0B]/15 text-[#F59E0B] shrink-0">
            <Warning size={20} weight="fill" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-semibold text-[var(--ink)]">
              {lang === "de" ? "Nummernkreise zurücksetzen?" : "Reset numbering?"}
            </h3>
            <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">
              {lang === "de"
                ? "Diese Aktion kann nicht rückgängig gemacht werden. Folgendes wird gelöscht:"
                : "This action cannot be undone. The following will be deleted:"}
            </p>
            <ul className="text-sm text-[var(--muted)] mt-2 ml-5 list-disc space-y-0.5">
              <li>{lang === "de" ? "Alle Angebote (auch Nachträge)" : "All quotes (incl. supplements)"}</li>
              <li>{lang === "de" ? "Alle Rechnungen" : "All invoices"}</li>
              <li>{lang === "de" ? "Alle Gutschriften" : "All credit notes"}</li>
              <li>{lang === "de" ? "Alle Aufmaße" : "All measurement protocols"}</li>
            </ul>
            <p className="text-sm text-[var(--muted)] mt-3 leading-relaxed">
              {lang === "de"
                ? <>Bleiben erhalten: <b>Kunden</b>, <b>Positionen</b>, <b>Kategorien</b>, <b>MwSt-Sätze</b> und <b>Firmen-Einstellungen</b>. Die nächsten Dokumente starten wieder bei <b>0100</b>.</>
                : <>Preserved: <b>customers</b>, <b>positions</b>, <b>categories</b>, <b>VAT rates</b>, and <b>company settings</b>. New documents will start at <b>0100</b>.</>}
            </p>
          </div>
          <button onClick={onClose} disabled={busy} className="text-[var(--muted)] hover:text-[var(--ink)] disabled:opacity-50" data-testid="numbering-reset-close">
            <X size={18} />
          </button>
        </div>

        {result ? (
          <div className="mt-4 border border-[var(--border)] bg-[var(--row-hover)] rounded-sm p-4" data-testid="numbering-reset-result">
            <div className="text-sm font-medium text-[var(--ink)] mb-2">
              {lang === "de" ? "Erfolgreich zurückgesetzt" : "Reset successful"}
            </div>
            <div className="text-xs text-[var(--muted)] space-y-1 font-mono">
              {Object.entries(result).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span>{k}</span>
                  <span className="text-[var(--ink)]">−{v}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <Btn variant="primary" onClick={onClose} data-testid="numbering-reset-done">
                {lang === "de" ? "Schließen" : "Close"}
              </Btn>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-4">
              <label className="label-overline block mb-1.5">
                {lang === "de"
                  ? <>Tippe <code className="text-[var(--ink)] bg-[var(--row-hover)] px-1 rounded-sm">RESET</code> zur Bestätigung</>
                  : <>Type <code className="text-[var(--ink)] bg-[var(--row-hover)] px-1 rounded-sm">RESET</code> to confirm</>}
              </label>
              <TextInput
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="RESET"
                autoFocus
                disabled={busy}
                data-testid="numbering-reset-confirm-input"
              />
            </div>
            {error && (
              <div className="mt-3 text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 rounded-sm" data-testid="numbering-reset-error">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={onClose} disabled={busy} data-testid="numbering-reset-cancel">
                {lang === "de" ? "Abbrechen" : "Cancel"}
              </Btn>
              <Btn variant="primary" onClick={submit} disabled={!canSubmit} data-testid="numbering-reset-submit">
                <Trash size={14} /> {busy
                  ? (lang === "de" ? "Wird zurückgesetzt…" : "Resetting…")
                  : (lang === "de" ? "Nummernkreise zurücksetzen" : "Reset numbering")}
              </Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const COUNTER_KINDS = [
  { kind: "invoice",     labelDe: "Rechnung",          labelEn: "Invoice",     prefix: "RSI" },
  { kind: "proforma",    labelDe: "Proforma",          labelEn: "Proforma",    prefix: "PRF" },
  { kind: "credit_note", labelDe: "Gutschrift",         labelEn: "Credit note", prefix: "GSI" },
  { kind: "quote",       labelDe: "Angebot",           labelEn: "Quote",       prefix: "A" },
  { kind: "aufmass",     labelDe: "Aufmaß",            labelEn: "Measurement", prefix: "AM" },
];

function CounterCard({ lang }) {
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const year = new Date().getFullYear();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/counters");
      const byKind = {};
      (data || []).forEach((c) => { if (c.year === year) byKind[c.kind] = c.value; });
      setCounters(COUNTER_KINDS.map((k) => ({
        ...k,
        value: byKind[k.kind] ?? 99,
        editValue: String(byKind[k.kind] ?? 99),
      })));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateEdit = (kind, v) => {
    setCounters((prev) => prev.map((c) => c.kind === kind ? { ...c, editValue: v } : c));
  };

  const saveCounter = async (kind) => {
    const row = counters.find((c) => c.kind === kind);
    const value = parseInt(row?.editValue || "0", 10);
    if (!Number.isFinite(value) || value < 0 || value > 999999) {
      toast.error(lang === "de" ? "Ungültiger Wert (0–999999)" : "Invalid value (0-999999)");
      return;
    }
    setSaving(kind);
    try {
      const { data } = await api.post("/admin/set-counter", { kind, value, year });
      toast.success(lang === "de" ? `Zähler ${kind}: nächste Nummer wird …${String(data.next_will_be).padStart(4, "0")}` : `Counter ${kind}: next will be …${String(data.next_will_be).padStart(4, "0")}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving("");
    }
  };

  if (loading) return null;

  return (
    <div className="border border-[var(--border)] bg-[var(--surface)] rounded-sm p-6" data-testid="settings-counter-card">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-[var(--brand)]/10 text-[var(--brand)] shrink-0">
          <FloppyDisk size={20} weight="regular" />
        </div>
        <div className="flex-1">
          <div className="label-overline text-[var(--brand)] mb-1">
            {lang === "de" ? "Nummernkreise anpassen" : "Adjust counters"}
          </div>
          <h3 className="font-heading text-lg font-semibold text-[var(--ink)]">
            {lang === "de" ? "Fortlaufende Zähler manuell setzen" : "Set consecutive counters manually"}
          </h3>
          <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">
            {lang === "de"
              ? "Der eingegebene Wert ist die LETZTE bereits vergebene Nummer. Der nächste Beleg erhält Wert+1. Beispiel: 106 → nächste Rechnung endet auf 0107. Nutzt Du dies, wenn Du versehentlich Test-Belege gelöscht hast und weiterzählen möchtest, ohne Nummern zu wiederholen."
              : "The value entered is the LAST already-assigned number. The next document gets value+1. Example: 106 → next invoice ends in 0107."}
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {counters.map((c) => (
              <div key={c.kind} className="flex items-center gap-2 border border-[var(--border)] rounded-sm px-3 py-2" data-testid={`counter-row-${c.kind}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[var(--muted-2)]">{lang === "de" ? c.labelDe : c.labelEn}</div>
                  <div className="text-xs text-[var(--muted)] font-mono truncate">
                    {c.prefix}-{year}-{String(c.value).padStart(4, "0")}
                    {" → "}
                    {c.prefix}-{year}-{String(c.value + 1).padStart(4, "0")}
                  </div>
                </div>
                <input
                  type="number"
                  min={0}
                  max={999999}
                  value={c.editValue}
                  onChange={(e) => updateEdit(c.kind, e.target.value)}
                  className="w-20 text-sm px-2 py-1 border border-[var(--border)] bg-[var(--bg)] rounded-sm tabular-nums"
                  data-testid={`counter-input-${c.kind}`}
                />
                <Btn variant="secondary" onClick={() => saveCounter(c.kind)} disabled={saving === c.kind || parseInt(c.editValue, 10) === c.value} className="h-8" data-testid={`counter-save-${c.kind}`}>
                  {saving === c.kind ? "…" : (lang === "de" ? "Setzen" : "Set")}
                </Btn>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

